import { toast as sonnerToast } from "sonner";

/**
 * Thin wrapper around sonner that bakes in the project's UX rules:
 *   - success / info: 4s auto-dismiss
 *   - warning:        8s auto-dismiss
 *   - error:          sticky (no auto-dismiss; user must close)
 *
 * Also exposes a promise() helper and a destructive() helper that
 * includes an Undo action by default.
 *
 * Usage:
 *   import { toast } from "@/components/shared/toast";  // or relative path
 *   toast.success("Job created");
 *   toast.error("Couldn't save", { description: err.message });
 *   toast.warning("Send window closes in 5 min");
 *   toast.promise(saveJob(), {
 *     loading: "Saving…",
 *     success: "Saved",
 *     error: (e) => `Failed: ${e.message}`,
 *   });
 *   toast.destructive("Job archived", { onUndo: () => unarchive() });
 */
const DURATIONS = {
  success: 4000,
  info: 4000,
  warning: 8000,
  error: Infinity, // sticky — must be dismissed manually
};

export const toast = {
  success(message, opts = {}) {
    return sonnerToast.success(message, { duration: DURATIONS.success, ...opts });
  },
  info(message, opts = {}) {
    return sonnerToast.info(message, { duration: DURATIONS.info, ...opts });
  },
  warning(message, opts = {}) {
    return sonnerToast.warning(message, { duration: DURATIONS.warning, ...opts });
  },
  error(message, opts = {}) {
    return sonnerToast.error(message, { duration: DURATIONS.error, ...opts });
  },
  message(message, opts = {}) {
    return sonnerToast(message, { duration: DURATIONS.info, ...opts });
  },
  promise(promise, msgs) {
    return sonnerToast.promise(promise, msgs);
  },
  dismiss(id) {
    return sonnerToast.dismiss(id);
  },
  /**
   * Destructive-with-undo. Use after archive / delete / reject actions.
   * Caller passes onUndo callback; we render a short-duration toast with
   * an Undo button. Stays visible long enough to act on (10s) without
   * being intrusive.
   */
  destructive(message, { onUndo, description, duration = 10000 } = {}) {
    return sonnerToast(message, {
      description,
      duration,
      action: onUndo
        ? {
            label: "Undo",
            onClick: onUndo,
          }
        : undefined,
    });
  },
};

export default toast;
